import { createFileRoute } from "@tanstack/react-router";
import { SEOPage } from "@/components/site/SEOPage";
import { Check, List, Info, Search, Key, ShieldCheck, HelpCircle, Zap, FileText, Globe, Smartphone, Mail, MessageSquare, Database, Lock, UserCheck } from "lucide-react";

const ArticlePage = () => {
  const breadcrumbs = [
    { label: "Elite", href: "/" },
    { label: "Blog", href: "/blog" },
    { label: "Como Encontrar Informações Necessárias para Configuração IPTV", href: "/blog/como-encontrar-informacoes-configuracao-iptv" }
  ];

  const faq = [
    {
      question: "Onde meus dados de acesso são enviados?",
      answer: "Por padrão, enviamos seus dados para o e-mail de cadastro. Se você solicitou um teste via WhatsApp, as credenciais serão entregues diretamente na conversa com o consultor."
    },
    {
      question: "O que fazer se os dados não chegarem?",
      answer: "Verifique sua pasta de SPAM ou Promoções. Se após 5 minutos não receber nada, entre em contato com o suporte informando o comprovante de pagamento."
    },
    {
      question: "Posso mudar meu usuário?",
      answer: "Geralmente não. O usuário é gerado pelo sistema e serve como sua ID no servidor. No entanto, você pode solicitar a troca da senha a qualquer momento."
    },
    {
      question: "Por que a URL tem números no final (ex: :8080)?",
      answer: "Esses números representam a 'Porta' de comunicação do servidor. Sem eles, o aplicativo não consegue encontrar a entrada correta do sinal."
    }
  ];

  const benefits = [
    {
      title: "Clareza de Dados",
      description: "Informações estruturadas para evitar confusão durante o login.",
      icon: <FileText className="w-6 h-6" />
    },
    {
      title: "Recuperação Ágil",
      description: "Canais seguros para recuperar seus dados se você os perder.",
      icon: <UserCheck className="w-6 h-6" />
    },
    {
      title: "Segurança de Entrega",
      description: "Protocolos que garantem que apenas você receba suas credenciais.",
      icon: <Lock className="w-6 h-6" />
    }
  ];

  const howItWorks = [
    {
      title: "Gatilho de Pedido",
      description: "Seu pagamento é aprovado ou o teste é liberado."
    },
    {
      title: "Geração Automática",
      description: "O servidor cria sua linha exclusiva na P2 PRO."
    },
    {
      title: "Despacho Seguro",
      description: "Os dados são disparados para seus canais de contato."
    },
    {
      title: "Armazenamento Local",
      description: "Você salva as informações para uso em múltiplos aparelhos."
    }
  ];

  const diferenciais = [
    {
      title: "One-Click Config",
      description: "Fornecemos um link inteligente que configura seu app apenas ao clicar.",
      icon: <Zap className="w-6 h-6" />
    },
    {
      title: "Central do Assinante",
      description: "Acesse seus dados técnicos 24h por dia através do nosso portal exclusivo.",
      icon: <Database className="w-6 h-6" />
    }
  ];

  const sections = [
    {
      title: "1. Introdução: O Mapa do seu Entretenimento",
      content: (
        <div className="prose prose-invert max-w-none">
          <p className="text-lg leading-relaxed text-muted-foreground mb-6">
            O momento em que você recebe seus dados de acesso ao IPTV é o início da sua jornada na <strong>P2 PRO</strong>. No entanto, para quem está começando, deparar-se com siglas como M3U, Host, EPG e Portas pode ser confuso. Encontrar e interpretar essas informações corretamente é 50% do caminho para uma configuração sem estresse.
          </p>
          <p className="text-lg leading-relaxed text-muted-foreground mb-6">
            Neste guia denso, ensinamos você a 'ler' os dados de configuração profissional. Mostramos onde cada informação fica escondida, como identificar o que é o quê e como organizar esses dados para que você possa configurar qualquer dispositivo em segundos, sem depender de suporte técnico.
          </p>
        </div>
      )
    },
    {
      title: "2. Os 3 Pilares: Desvendando as Credenciais",
      type: "featured" as const,
      content: (
        <div className="grid md:grid-cols-3 gap-6">
          <div className="p-8 bg-black/20 rounded-3xl border border-white/5 space-y-4">
            <div className="w-12 h-12 bg-electric/10 rounded-2xl flex items-center justify-center text-electric"><Globe /></div>
            <h4 className="font-bold text-white uppercase italic tracking-tighter text-sm">A URL do Host</h4>
            <p className="text-xs text-muted-foreground">É o endereço digital do nosso servidor. Ex: <code>http://p2pro.stream:8080</code>. Sem este endereço, seu app é apenas uma caixa vazia.</p>
          </div>
          <div className="p-8 bg-black/20 rounded-3xl border border-white/5 space-y-4">
            <div className="w-12 h-12 bg-electric/10 rounded-2xl flex items-center justify-center text-electric"><UserCheck /></div>
            <h4 className="font-bold text-white uppercase italic tracking-tighter text-sm">O Username</h4>
            <p className="text-xs text-muted-foreground">Seu código único de cliente. É o que permite ao servidor identificar seu plano e liberar os conteúdos correspondentes.</p>
          </div>
          <div className="p-8 bg-black/20 rounded-3xl border border-white/5 space-y-4">
            <div className="w-12 h-12 bg-electric/10 rounded-2xl flex items-center justify-center text-electric"><Key /></div>
            <h4 className="font-bold text-white uppercase italic tracking-tighter text-sm">A Password</h4>
            <p className="text-xs text-muted-foreground">Sua chave secreta de decodificação. Garante que apenas o seu hardware possa acessar o sinal da sua linha.</p>
          </div>
        </div>
      )
    },
    {
      title: "3. Onde Localizar cada Informação",
      content: (
        <div className="prose prose-invert max-w-none space-y-8">
          <div className="flex gap-6 items-start">
            <Mail className="w-8 h-8 text-electric flex-shrink-0" />
            <div>
              <h4 className="text-white text-xl font-bold mb-2">Canal 1: E-mail de Boas-Vindas</h4>
              <p className="text-muted-foreground">Assim que sua ativação é processada, nosso sistema dispara um e-mail estruturado. Procure pelo assunto <strong>"Acesso Liberado - P2 PRO Elite"</strong>. Nele, os dados estão organizados em blocos: um para Xtream Codes e outro com o Link M3U completo.</p>
            </div>
          </div>
          <div className="flex gap-6 items-start">
            <MessageSquare className="w-8 h-8 text-electric flex-shrink-0" />
            <div>
              <h4 className="text-white text-xl font-bold mb-2">Canal 2: WhatsApp de Atendimento</h4>
              <p className="text-muted-foreground">Se você contratou via consultor, ele enviará um 'Card de Acesso'. <strong>Dica de Ouro:</strong> Copie essa mensagem e salve nos seus 'Favoritos' ou em um grupo privado no WhatsApp (seu bloco de notas) para acesso rápido futuro.</p>
            </div>
          </div>
          <div className="flex gap-6 items-start">
            <Database className="w-8 h-8 text-electric flex-shrink-0" />
            <div>
              <h4 className="text-white text-xl font-bold mb-2">Canal 3: Painel do Cliente (Web)</h4>
              <p className="text-muted-foreground">Acesse <code>minha.p2pro.vip</code> com seu e-mail e CPF. Na aba <strong>"Meus Serviços"</strong>, você terá o botão 'Ver Credenciais'. Este é o método mais seguro, pois não depende de entregas externas de e-mail.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "4. Interpretando Erros de Dados",
      content: (
        <div className="prose prose-invert max-w-none">
          <p className="text-muted-foreground mb-6">Muitas vezes você tem os dados, mas eles parecem não funcionar. Siga este protocolo de conferência:</p>
          <ul className="space-y-4">
            <li className="flex items-start gap-4">
              <Check className="w-6 h-6 text-electric mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">Confusão entre 'I' (i maiúsculo) e 'l' (L minúsculo)</h5>
                <p className="text-sm text-muted-foreground">Em algumas fontes, eles são idênticos. Se o login falhar, tente trocar um pelo outro.</p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <Check className="w-6 h-6 text-electric mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">A Importância da Porta (:8080)</h5>
                <p className="text-sm text-muted-foreground">Alguns aplicativos têm um campo separado para a porta. Se o campo for apenas 'Host', coloque <code>http://url.com:8080</code>. Se tiver o campo 'Port', coloque apenas <code>8080</code>.</p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <Info className="w-6 h-6 text-amber-500 mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">Link M3U vs Xtream Codes</h5>
                <p className="text-sm text-muted-foreground">Se o app pedir um 'Link', use a URL longa. Se pedir 'Usuário e Senha', use os dados curtos. Nunca misture os dois no mesmo login.</p>
              </div>
            </li>
          </ul>
        </div>
      )
    }
  ];

  return (
    <div className="relative">
      <SEOPage
        category="Central de Ajuda IPTV"
        title="Como Localizar seus Dados de Configuração IPTV"
        subtitle="Onde encontrar seu Usuário, Senha e URL de servidor sem depender de suporte e em tempo recorde."
        heroImage="https://images.unsplash.com/photo-1454165205744-3b78555e5572?auto=format&fit=crop&q=80&w=1200"
        introParagraphs={[
          "O sucesso da sua configuração de IPTV depende da qualidade das informações que você tem em mãos. Muitas vezes, o suporte técnico é acionado por questões simples que poderiam ser resolvidas se o usuário soubesse onde encontrar e como interpretar seus próprios dados de acesso.",
          "Neste guia denso da Central de Ajuda P2 PRO, desmistificamos os dados de configuração. Mostramos todos os canais oficiais de entrega de credenciais, ensinamos a diferenciar termos técnicos e damos dicas profissionais para você organizar seus acessos.",
          "Transforme-se em um usuário avançado e tome o controle total da sua infraestrutura de entretenimento digital hoje mesmo."
        ]}
        breadcrumbs={breadcrumbs}
        faq={faq}
        benefits={benefits}
        howItWorks={howItWorks}
        diferenciais={diferenciais}
        sections={sections}
        ctaTitle="🔍 Não Recebeu seus Dados?"
        ctaDescription="Checou tudo e ainda está sem seus acessos? Nossa central de liberação manual está online agora para te ajudar."
        ctaButtonText="Localizar Meus Dados Agora"
      />
    </div>
  );
};

export const Route = createFileRoute("/blog/como-encontrar-informacoes-configuracao-iptv")({
  head: () => ({
    meta: [
      { title: "Onde Encontrar Dados de Configuração IPTV - Guia Prático Denso" },
      { name: "description", content: "Manual completo sobre localização de credenciais IPTV. Aprenda a encontrar usuário, senha e URL no e-mail, WhatsApp e Painel do Cliente P2 PRO." },
    ],
  }),
  component: ArticlePage,
});
